import * as v from "@valibot/valibot";

// ============================================================
// API Schemas
// ============================================================

import { Address, Hex, UnsignedInteger } from "../../_schemas.ts";

/**
 * Distribute funds from a vault between followers.
 * @see null
 */
export const VaultDistributeRequest = /* @__PURE__ */ (() => {
  return v.object({
    /** Action to perform. */
    action: v.object({
      /** Type of action. */
      type: v.literal("vaultDistribute"),
      /** Vault address. */
      vaultAddress: Address,
      /**
       * Amount to distribute (float * 1e6).
       * Set to 0 to close the vault.
       */
      usd: UnsignedInteger,
    }),
    /** Nonce (timestamp in ms) used to prevent replay attacks. */
    nonce: UnsignedInteger,
    /** ECDSA signature components. */
    signature: v.object({
      /** First 32-byte component. */
      r: v.pipe(Hex, v.length(66)),
      /** Second 32-byte component. */
      s: v.pipe(Hex, v.length(66)),
      /** Recovery identifier. */
      v: v.picklist([27, 28]),
    }),
    /** Expiration time of the action. */
    expiresAfter: v.optional(UnsignedInteger),
  });
})();
export type VaultDistributeRequest = v.InferOutput<typeof VaultDistributeRequest>;

/**
 * Successful response without specific data or error response.
 * @see null
 */
export type VaultDistributeResponse =
  | {
    /** Successful status. */
    status: "ok";
    /** Response details. */
    response: {
      /** Type of response. */
      type: "default";
    };
  }
  | {
    /** Error status. */
    status: "err";
    /** Error message. */
    response: string;
  };

// ============================================================
// Execution Logic
// ============================================================

import { parse } from "../../../_base.ts";
import { canonicalize } from "../../../signing/mod.ts";
import {
  type ExchangeConfig,
  type ExcludeErrorResponse,
  executeL1Action,
  type ExtractRequestOptions,
} from "./_base/mod.ts";

/** Schema for action fields (excludes request-level system fields). */
const VaultDistributeActionSchema = /* @__PURE__ */ (() => {
  return v.object(VaultDistributeRequest.entries.action.entries);
})();

/** Action parameters for the {@linkcode vaultDistribute} function. */
export type VaultDistributeParameters = Omit<v.InferInput<typeof VaultDistributeActionSchema>, "type">;

/** Request options for the {@linkcode vaultDistribute} function. */
export type VaultDistributeOptions = ExtractRequestOptions<v.InferInput<typeof VaultDistributeRequest>>;

/** Successful variant of {@linkcode VaultDistributeResponse} without errors. */
export type VaultDistributeSuccessResponse = ExcludeErrorResponse<VaultDistributeResponse>;

/**
 * Distribute funds from a vault between followers.
 *
 * Signing: L1 Action.
 *
 * @param config General configuration for Exchange API requests.
 * @param params Parameters specific to the API request.
 * @param opts Request execution options.
 * @return Successful response without specific data.
 *
 * @throws {ValidationError} When the request parameters fail validation (before sending).
 * @throws {TransportError} When the transport layer throws an error.
 * @throws {ApiRequestError} When the API returns an unsuccessful response.
 *
 * @example
 * ```ts
 * import { HttpTransport } from "@nktkas/hyperliquid";
 * import { vaultDistribute } from "@nktkas/hyperliquid/api/exchange";
 * import { privateKeyToAccount } from "npm:viem/accounts";
 *
 * const wallet = privateKeyToAccount("0x..."); // viem or ethers
 * const transport = new HttpTransport(); // or `WebSocketTransport`
 *
 * await vaultDistribute({ transport, wallet }, {
 *   vaultAddress: "0x...",
 *   usd: 10 * 1e6,
 * });
 * ```
 *
 * @see null
 */
export function vaultDistribute(
  config: ExchangeConfig,
  params: VaultDistributeParameters,
  opts?: VaultDistributeOptions,
): Promise<VaultDistributeSuccessResponse> {
  const action = canonicalize(
    VaultDistributeActionSchema,
    parse(VaultDistributeActionSchema, { type: "vaultDistribute", ...params }),
  );
  return executeL1Action(config, action, opts);
}
